/**
 * Temas del vocabulario — la lista CERRADA con la que se etiqueta cada palabra
 * que el coach detecta en una charla (`vocab_entries.topic`).
 *
 * Por qué cerrada (card #348, hallazgo #177): el distiller la escribía libre
 * ("work, travel, food, technology, family, etc.") y en la base aparecieron 20
 * valores contra los 11 que la app sabía mostrar — `sports`, `music`, `small
 * talk`… se veían en inglés crudo y no se podía filtrar por tema. La lista
 * sale de dos fuentes que ya existían: los 11 temas del front y los temas con
 * los que el coach abre una charla (`kickoff-cues.ts`: música, películas y
 * series, deportes, leer/aprender…).
 *
 * Contrato: lo que escribe el distiller pasa por {@link normalizeVocabTopic}
 * (sinónimo conocido → su tema; resto → null, "sin tema"), y la API rechaza
 * cualquier otro valor. Los nombres en español viven en el front.
 */

export const VOCAB_TOPIC_IDS = [
  'nature',
  'travel',
  'work',
  'food',
  'social',
  'technology',
  'family',
  'health',
  'home',
  'leisure',
  'shopping',
  'sports',
  'music',
  'entertainment',
  'learning',
] as const;
export type VocabTopic = (typeof VOCAB_TOPIC_IDS)[number];

/**
 * Sinónimos que el distiller escribía (o es esperable que escriba) → su tema.
 * Lo que no está acá ni en la lista queda sin tema: mejor "sin tema" que un
 * tema inventado que no se puede mostrar ni filtrar.
 */
const VOCAB_TOPIC_ALIASES: ReadonlyMap<string, VocabTopic> = new Map(
  Object.entries({
    sport: 'sports',
    exercise: 'sports',
    fitness: 'sports',
    gym: 'sports',
    songs: 'music',
    movies: 'entertainment',
    movie: 'entertainment',
    film: 'entertainment',
    films: 'entertainment',
    series: 'entertainment',
    tv: 'entertainment',
    television: 'entertainment',
    hobby: 'leisure',
    hobbies: 'leisure',
    lifestyle: 'leisure',
    'free time': 'leisure',
    conversation: 'social',
    'small talk': 'social',
    friends: 'social',
    friendship: 'social',
    relationships: 'social',
    dating: 'social',
    neighborhood: 'home',
    neighbourhood: 'home',
    city: 'home',
    'daily life': 'home',
    routine: 'home',
    household: 'home',
    weather: 'nature',
    animals: 'nature',
    pets: 'nature',
    environment: 'nature',
    cooking: 'food',
    restaurants: 'food',
    drinks: 'food',
    coffee: 'food',
    business: 'work',
    career: 'work',
    job: 'work',
    jobs: 'work',
    office: 'work',
    education: 'learning',
    school: 'learning',
    study: 'learning',
    studying: 'learning',
    language: 'learning',
    languages: 'learning',
    reading: 'learning',
    books: 'learning',
  } satisfies Record<string, VocabTopic>),
);

export function isVocabTopic(value: unknown): value is VocabTopic {
  return typeof value === 'string' && (VOCAB_TOPIC_IDS as readonly string[]).includes(value);
}

/** Tema libre (como lo escribe un modelo) → tema de la lista, o null. */
export function normalizeVocabTopic(raw: string | null | undefined): VocabTopic | null {
  if (raw == null) return null;
  const key = raw.trim().toLowerCase();
  if (isVocabTopic(key)) return key;
  // Map y no objeto literal: la clave la escribe un modelo, y `obj['constructor']`
  // devolvería algo del prototipo.
  return VOCAB_TOPIC_ALIASES.get(key) ?? null;
}
